import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import supabase from '../services/supabaseClient';

export interface User {
  id: string;
  name?: string;
  email: string;
  phone?: string;
  emailConfirmed?: boolean; // new flag to indicate email confirmation status
}

interface AuthResponse {
  data: {
    user: any;
    session: any;
    weakPassword?: any;
  } | null;
  user?: any;
  session?: any;
  weakPassword?: any;
  error: any;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<AuthResponse>;
  logout: () => Promise<void>;
  register: (name: string, email: string, phone: string, password: string) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  confirmEmail: (email: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  isAuthenticated: false,
  login: async () => ({ data: null, error: null }),
  logout: async () => {},
  register: async () => {},
  forgotPassword: async () => {},
  confirmEmail: async () => {}
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const isAuthenticated = !!user;

  // Helper to derive confirmation status from Supabase user object
  const isSupabaseUserConfirmed = (supUser: any) => {
    if (!supUser) return false;
    return !!(supUser.confirmed_at || supUser.email_confirmed_at || supUser.email_confirmed);
  };

  // Verifica a sessão do usuário ao carregar o componente
  useEffect(() => {
    const checkAuth = async () => {
      try {
        setLoading(true);
        
        // Tenta recuperar usuário do localStorage primeiro (para evitar flash de tela não autenticada)
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          try {
            const parsedUser = JSON.parse(storedUser) as User;
            if (parsedUser && parsedUser.id) {
              setUser(parsedUser);
              console.log('✅ Usuário recuperado do localStorage:', parsedUser.email);
            }
          } catch (parseError) {
            console.warn('⚠️ Erro ao analisar usuário do localStorage:', parseError);
          }
        }
        
        // Verifica se há uma sessão ativa no Supabase
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session?.user) {
          // Atualiza o estado do usuário com base na sessão
          const userData: User = {
            id: session.user.id,
            email: session.user.email || '',
            name: session.user.user_metadata?.name,
            phone: session.user.user_metadata?.phone,
            emailConfirmed: isSupabaseUserConfirmed(session.user),
          };
          setUser(userData);
          localStorage.setItem('user', JSON.stringify(userData));
          console.log('✅ Sessão ativa encontrada para:', userData.email);
        } else {
          // Se não há sessão no Supabase, mas temos usuário no localStorage,
          // tentamos manter o usuário para evitar perda de sessão entre ambientes
          const storedUser = localStorage.getItem('user');
          if (storedUser) {
            try {
              const parsedUser = JSON.parse(storedUser) as User;
              if (parsedUser && parsedUser.id) {
                console.log('⚠️ Sessão Supabase não encontrada, mas mantendo usuário do localStorage:', parsedUser.email);
                // Mantém o usuário do localStorage
                return;
              }
            } catch (parseError) {
              console.warn('⚠️ Erro ao analisar usuário do localStorage:', parseError);
            }
          }
          
          // Se não há sessão no Supabase nem usuário válido no localStorage, limpa o estado
          console.log('❌ Nenhuma sessão ativa encontrada');
          setUser(null);
          localStorage.removeItem('user');
        }
      } catch (error) {
        console.error('❌ Erro ao verificar sessão:', error);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();

    // Configura o listener para mudanças na autenticação
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('🔄 Evento de autenticação:', event);
      
      if (session?.user) {
        const userData: User = {
          id: session.user.id,
          email: session.user.email || '',
          name: session.user.user_metadata?.name,
          phone: session.user.user_metadata?.phone,
          emailConfirmed: isSupabaseUserConfirmed(session.user),
        };
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
        console.log('✅ Usuário atualizado após evento de autenticação:', userData.email);
      } else if (event === 'SIGNED_OUT') {
        // Limpa o estado apenas em caso de logout explícito
        setUser(null);
        localStorage.removeItem('user');
        console.log('🚪 Usuário desconectado após evento de autenticação');
      } else {
        // Para outros eventos sem sessão, verificamos se temos um usuário no localStorage
        // antes de limpar o estado (para evitar perda de sessão entre ambientes)
        const storedUser = localStorage.getItem('user');
        if (!storedUser) {
          setUser(null);
          console.log('❌ Nenhum usuário encontrado no localStorage após evento:', event);
        } else {
          try {
            const parsedUser = JSON.parse(storedUser) as User;
            if (parsedUser && parsedUser.id) {
              console.log('⚠️ Evento sem sessão, mas mantendo usuário do localStorage:', parsedUser.email);
              setUser(parsedUser);
            } else {
              setUser(null);
              localStorage.removeItem('user');
              console.log('❌ Usuário inválido no localStorage após evento:', event);
            }
          } catch (parseError) {
            console.warn('⚠️ Erro ao analisar usuário do localStorage após evento:', parseError);
            setUser(null);
            localStorage.removeItem('user');
          }
        }
      }
    });

    // Limpa o listener ao desmontar o componente
    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  const login = async (email: string, password: string) => {
    try {
      setLoading(true);
      console.log('🔐 Tentando login para:', email);
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        // Verifica se o erro é de e-mail não confirmado
        if (error.message === 'Email not confirmed' || error.code === 'email_not_confirmed') {
          console.log('⚠️ E-mail não confirmado, criando sessão manualmente...');
          
          try {
            // Vamos criar uma sessão manualmente para o usuário
            // Primeiro, vamos obter o usuário pelo email
            const { data: userData, error: userError } = await supabase
              .from('usuarios')
              .select('id, email, name, phone')
              .eq('email', email)
              .single();
            
            if (userError) {
              console.error('❌ Erro ao buscar usuário:', userError);
              return { data: null, error: userError };
            }
            
            if (userData) {
              // Criar um objeto de usuário manualmente
              const manualUser: User = {
                id: userData.id,
                email: userData.email,
                name: userData.name,
                phone: userData.phone,
                emailConfirmed: true // Forçamos como true
              };
              
              // Definir o usuário no estado e localStorage
              setUser(manualUser);
              localStorage.setItem('user', JSON.stringify(manualUser));
              
              // Retornar um objeto simulando um login bem-sucedido
              return {
                data: {
                  user: manualUser,
                  session: { user: manualUser }
                },
                error: null
              };
            }
          } catch (err) {
            console.error('❌ Erro ao criar sessão manual:', err);
          }
          
          // Se chegou aqui, tenta o login normal mesmo com erro
          console.log('⚠️ Tentando login normal mesmo com email não confirmado...');
        }
        
        console.error('❌ Erro no login:', error);
        return { data: null, error };
      }

      if (data?.user) {
        console.log('✅ Login bem-sucedido para:', email);
        const userData: User = {
          id: data.user.id,
          email: data.user.email || email,
          name: data.user.user_metadata?.name,
          phone: data.user.user_metadata?.phone,
          emailConfirmed: isSupabaseUserConfirmed(data.user),
        };
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
      }

      return { data, error: null };
    } catch (error) {
      console.error('❌ Erro inesperado no login:', error);
      return { data: null, error };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      console.log('🚪 Efetuando logout...');

      // Check if there is an active session first — avoid calling signOut when no session exists
      const { data: { session } } = await supabase.auth.getSession();

      if (session) {
        const { error } = await supabase.auth.signOut();

        if (error) {
          console.error('❌ Erro ao fazer logout:', error);
          // continue to clear local state even if the remote signOut failed
        } else {
          console.log('✅ Logout realizado com sucesso');
        }
      } else {
        console.warn('⚠️ Nenhuma sessão ativa encontrada. Limpando estado local.');
      }

      // Local cleanup
      setUser(null);
      localStorage.removeItem('user');

      // --- Opção B: remover explicitamente as tags de estilo / script da Vitrine do <head> ---
      try {
        if (typeof window !== 'undefined' && document && document.head) {
          // Remove links específicos que podem ter sido injetados pela Vitrine
          const linkSelectors = ['link[data-vitrine-styles]', 'link#dark-styles', 'link[href*="/vitrine_assets/"]'];
          linkSelectors.forEach(sel => {
            document.querySelectorAll(sel).forEach(el => el.remove());
          });

          // Remove scripts que possam ter sido injetados pela Vitrine
          document.querySelectorAll('script[src*="/vitrine_assets/"], script[data-vitrine-script]').forEach(s => s.remove());

          // Remove qualquer handle global exposto pela Vitrine
          try { delete (window as any).onAppLogout; } catch (e) { /* ignore */ }

          console.log('🧹 Assets da vitrine removidos do <head> com sucesso');
        }
      } catch (cleanupErr) {
        console.warn('⚠️ Erro ao limpar assets da vitrine:', cleanupErr);
      }

      // Dispatch a global event so other parts of the app can react and navigate as needed
      try {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new Event('auth:logged-out'));
        }
      } catch (e) {
        /* ignore */
      }
    } catch (err) {
      console.error('❌ Erro inesperado ao fazer logout:', err);
      // Ensure local cleanup so the UI is logged out even if Supabase call fails
      setUser(null);
      localStorage.removeItem('user');

      // Attempt same cleanup como acima, mesmo em erro
      try {
        if (typeof window !== 'undefined' && document && document.head) {
          document.querySelectorAll('link[data-vitrine-styles], link#dark-styles, link[href*="/vitrine_assets/"]')
            .forEach(el => el.remove());
          document.querySelectorAll('script[src*="/vitrine_assets/"], script[data-vitrine-script]').forEach(s => s.remove());
          try { delete (window as any).onAppLogout; } catch (e) { /* ignore */ }
        }
        try {
          if (typeof window !== 'undefined') window.dispatchEvent(new Event('auth:logged-out'));
        } catch (e) { /* ignore */ }
      } catch (e) {
        /* ignore */
      }
    }
  };

  const forgotPassword = async (email: string): Promise<void> => {
    try {
      setLoading(true);
      console.log('📧 Enviando e-mail de redefinição para:', email);
      
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/redefinir-senha`,
      });

      if (error) {
        console.error('❌ Erro ao enviar e-mail de redefinição:', error);
        throw error;
      }

      console.log('✅ E-mail de redefinição enviado com sucesso');
    } catch (error) {
      console.error('❌ Erro inesperado ao solicitar redefinição de senha:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const register = async (name: string, email: string, phone: string, password: string): Promise<void> => {
    // Função auxiliar para registrar etapas
    const logStepCompletion = (step: string, success: boolean, error?: Error) => {
      console.log(`[${success ? '✅' : '❌'}] ${step}: ${success ? 'Concluído com sucesso' : 'Falha'}`);
      if (error) {
        console.error('Erro detalhado:', error);
      }
    };
    
    const authStart = Date.now();
    
    try {
      console.log('🔍 Iniciando processo de registro para:', email);
      
      // 1. Validação dos dados de entrada
      if (!email || !password) {
        const error = new Error('E-mail e senha são obrigatórios');
        logStepCompletion('validation', false, error);
        throw error;
      }
      
      // 2. Obter o IP do cliente
      console.log('🌐 Obtendo endereço IP do cliente...');
      const ipResponse = await fetch('https://api.ipify.org?format=json');
      const ipData = await ipResponse.json();
      const userIp = ipData.ip;
      
      if (!userIp) {
        const error = new Error('Não foi possível obter o endereço IP');
        logStepCompletion('ip_lookup', false, error);
        throw error;
      }
      
      console.log('📡 IP do cliente:', userIp);
      logStepCompletion('ip_lookup', true);
      
      // 3. Verificar limite de cadastros por IP
      console.log('🔍 Verificando limite de cadastros por IP...');
      const { data: ipCheck, error: ipCheckError } = await supabase
        .from('signup_ip_control')
        .select('*')
        .eq('ip', userIp)
        .eq('blocked', true)
        .gt('created_at', new Date(Date.now() - 30 * 60 * 1000).toISOString()); // Últimos 30 minutos
      
      if (ipCheckError) {
        console.warn('⚠️ Não foi possível verificar limite de IP:', ipCheckError);
        // Não bloqueia o cadastro se a verificação de IP falhar (ex: RLS)
        logStepCompletion('rate_limit_check', true);
      } else if (ipCheck && ipCheck.length > 0) {
        const error = new Error('Limite de cadastros excedido para este IP. Tente novamente mais tarde.');
        logStepCompletion('rate_limit_check', false, error);
        throw error;
      } else {
        logStepCompletion('rate_limit_check', true);
      }
      
      // 4. Verificar se o e-mail já existe na tabela de usuários
      console.log('🔍 Verificando e-mail existente...');
      try {
        // Verifica na tabela de usuários
        const { data: existingUser, error: userCheckError } = await supabase
          .from('usuarios')
          .select('email')
          .eq('email', email)
          .maybeSingle(); // Usamos maybeSingle para evitar erros quando não encontra
        
        if (userCheckError) {
          console.warn('⚠️ Não foi possível verificar e-mail na tabela de usuários:', userCheckError);
          // Continuamos mesmo com erro, pois pode ser um problema de permissão
        } else if (existingUser) {
          const error = new Error('Este e-mail já está em uso.');
          logStepCompletion('email_check', false, error);
          throw error;
        }
        
        logStepCompletion('email_check', true);
      } catch (error) {
        console.error('❌ Erro ao verificar e-mail existente:', error);
        // Se houver erro na verificação, não bloqueia o cadastro
        // O Supabase Auth já faz essa verificação internamente
        logStepCompletion('email_check', false, error instanceof Error ? error : new Error(String(error)));
      }
      
      // 5. Criar usuário no Supabase Auth
      console.log('🔄 Criando usuário no Supabase Auth...');
      console.log('Attempting signUp with email:', email);
      // Registra o usuário no Supabase Auth com autoConfirm
      const { data, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name,
            phone,
            email_confirmed: true, // Tenta marcar o e-mail como confirmado nos metadados
            confirmed_at: new Date().toISOString(), // Adiciona timestamp de confirmação
            email_confirmed_at: new Date().toISOString() // Adiciona timestamp de confirmação de email
          },
          emailRedirectTo: `${window.location.origin}/login?message=email_confirmed` // Redireciona após confirmação
        }
      });
      
      if (signUpError) {
        console.error('❌ Erro ao criar usuário no Supabase Auth:', signUpError);
        console.error('SignUp error:', signUpError);
        throw signUpError;
      }
      
      console.log('SignUp response:', data);
      const user = data.user;
      
      if (!user) {
        const error = new Error('Não foi possível criar o usuário. Por favor, tente novamente.');
        logStepCompletion('auth', false, error);
        throw error;
      }
      
      // 6. Inserir dados na tabela de usuários
      console.log('💾 Inserindo dados na tabela de usuários...');
      try {
        // Tenta inserir apenas os campos essenciais
        const userData: Record<string, any> = {
          id: user.id,
          name,
          email,
          phone
        };

        // Tenta inserir sem created_at primeiro
        const { error: userInsertError } = await supabase
          .from('usuarios')
          .insert([userData]);
        
        if (userInsertError) {
          console.warn('⚠️ Erro ao inserir usuário, tentando com campos mínimos...', userInsertError);
          
          // Se falhar, tenta apenas com campos obrigatórios
          const minimalUserData = {
            id: user.id,
            email
          };
          
          const { error: minimalInsertError } = await supabase
            .from('usuarios')
            .insert([minimalUserData]);
          
          if (minimalInsertError) {
            console.warn('⚠️ Não foi possível inserir na tabela de usuários:', minimalInsertError);
            // Não lançamos erro, pois o usuário já foi criado no Auth
          } else {
            console.log('✅ Dados mínimos inseridos na tabela de usuários');
          }
        }
      } catch (insertError) {
        console.error('❌ Erro ao inserir usuário na tabela:', insertError);
        // Não interrompemos o fluxo se falhar, apenas registramos o erro
        // O usuário já foi criado no Auth, então o registro foi bem sucedido
      }
      
      logStepCompletion('user_table_insert', true);
      
      // 7. Registrar tentativa de cadastro na tabela de controle de IP
      console.log('📝 Registrando tentativa de cadastro...');
      const { error: ipInsertError } = await supabase
        .from('signup_ip_control')
        .insert([
          {
            ip: userIp,
            email,
            blocked: false,
            created_at: new Date().toISOString()
          }
        ]);
      
      if (ipInsertError) {
        console.error('❌ Erro ao registrar tentativa de cadastro:', ipInsertError);
        // Não interrompemos o fluxo se falhar, apenas registramos o erro
      } else {
        logStepCompletion('ip_control_insert', true);
      }
      
      // Não podemos confirmar o e-mail programaticamente no cliente
      // Essa é uma configuração do servidor que só pode ser alterada no painel de administração do Supabase
      
      // 9. Fazer login automático após o registro
      console.log('🔄 Tentando login automático após registro...');
      try {
        // Tenta fazer login diretamente após o registro
        const loginResult = await login(email, password);
        
        if (loginResult.error) {
          console.warn('⚠️ Não foi possível fazer login automático após registro:', loginResult.error);
          
          // Mesmo se o login falhar, definimos o usuário localmente para autenticar
          if (user) {
            const userData: User = {
              id: user.id,
              email: email,
              name: name,
              phone: phone,
              emailConfirmed: true, // Consideramos o email como confirmado
            };
            setUser(userData);
            localStorage.setItem('user', JSON.stringify(userData));
          }
        } else {
          console.log('✅ Login automático após registro realizado com sucesso');
        }
      } catch (loginError) {
        console.error('❌ Erro ao tentar login automático após registro:', loginError);
        
        // Mesmo se o login falhar, definimos o usuário localmente para autenticar
        if (user) {
          const userData: User = {
            id: user.id,
            email: email,
            name: name,
            phone: phone,
            emailConfirmed: true, // Consideramos o email como confirmado
          };
          setUser(userData);
          localStorage.setItem('user', JSON.stringify(userData));
        }
      }
      
      console.log('✅ Registro concluído com sucesso em', Date.now() - authStart, 'ms');
      logStepCompletion('registration', true);
    } catch (authError) {
      const error = authError instanceof Error ? authError : new Error('Erro desconhecido ao criar conta');
      logStepCompletion('auth', false, error);
      throw error;
    }
  };

  // Função para confirmar o email de um usuário
  const confirmEmail = async (email: string): Promise<void> => {
    try {
      setLoading(true);
      console.log('🔄 Tentando confirmar email para:', email);
      
      // Buscar o usuário pelo email
      const { data: userData, error: userError } = await supabase
        .from('usuarios')
        .select('id')
        .eq('email', email)
        .single();
      
      if (userError) {
        console.error('❌ Erro ao buscar usuário:', userError);
        throw userError;
      }
      
      if (!userData) {
        console.error('❌ Usuário não encontrado para o email:', email);
        throw new Error('Usuário não encontrado');
      }
      
      // Atualizar os metadados do usuário para marcar o email como confirmado
      // Isso é uma solução alternativa, já que não podemos confirmar o email diretamente
      // através da API do Supabase no cliente
      const { error: updateError } = await supabase
        .from('usuarios')
        .update({ email_confirmed: true })
        .eq('id', userData.id);
      
      if (updateError) {
        console.error('❌ Erro ao atualizar confirmação de email:', updateError);
        throw updateError;
      }
      
      console.log('✅ Email confirmado com sucesso para:', email);
      
      // Se o usuário atual tem o mesmo email, atualizar o estado
      if (user && user.email === email) {
        const updatedUser = { ...user, emailConfirmed: true };
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }
    } catch (error) {
      console.error('❌ Erro ao confirmar email:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAuthenticated: !!user,
        login,
        logout,
        register,
        forgotPassword,
        confirmEmail,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
